import DataOps from '../../../data/DataOps';
import NotificationOps from '../../../data/NotificationOps';
import { Alert } from 'react-native';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { AttendanceUpdateType, computeNextAttendance, normalizeAttendanceRecord } from './utils';

const EMPTY_SUMMARY = {
    attendance: {},
    classData: {},
    studentData: {},
};

const toIsoString = (value: any): string => {
    if (!value) return new Date().toISOString();
    if (value?.toDate) return value.toDate().toISOString();
    if (value instanceof Date) return value.toISOString();
    if (typeof value === 'string') return value;
    return new Date().toISOString();
};

const toCommentUI = (item: any) => ({
    id: item?.id || item?.contentId || `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    text: item?.text || '',
    timestamp: toIsoString(item?.timestamp || item?.createdAt),
    author: item?.author || item?.updatedBy || 'Coach',
});

const toPhotoUI = (item: any) => ({
    id: item?.id || item?.contentId || `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    uri: item?.uri || item?.imageUrl || '',
    caption: item?.caption || '',
    timestamp: toIsoString(item?.timestamp || item?.createdAt),
    author: item?.author || item?.updatedBy || 'Coach',
});

const toCloudComment = (item: any, authorName: string) => ({
    contentId: item?.id || item?.contentId || `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    updatedBy: item?.author || item?.updatedBy || authorName,
    text: item?.text || '',
    createdAt: item?.timestamp ? new Date(item.timestamp) : (item?.createdAt || new Date()),
});

const toCloudMoment = (item: any, authorName: string) => ({
    contentId: item?.id || item?.contentId || `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    updatedBy: item?.author || item?.updatedBy || authorName,
    imageUrl: item?.uri || item?.imageUrl || '',
    createdAt: item?.timestamp ? new Date(item.timestamp) : (item?.createdAt || new Date()),
});

export const loadSessionDataForSession = async (sessionData: any) => {
    try {
        let children: any[] = [];
        const registrations = await DataOps.fetchActiveRegistrationsForSession(sessionData?.sessionId);
        const normalizedRegs = Array.isArray(registrations) && registrations.length > 0
            ? registrations.map((reg: any) => ({
                childId: reg.childId,
                childName: reg.childName,
                parentId: reg.userId || reg.parentId,
                parentName: reg.parentName,
            }))
            : Object.values(sessionData?.registeredChildren || {});

        if (normalizedRegs.length > 0) {
            const childPromises = normalizedRegs.map(async (childReg: any) => {
                const parentData = await DataOps.fetchDataFromFirebase('user', childReg.parentId);
                const child = parentData?.children?.find((c: any) => c.childId === childReg.childId);
                return {
                    userId: childReg.childId,
                    userName: childReg.childName,
                    childId: childReg.childId,
                    childName: childReg.childName,
                    parentId: childReg.parentId,
                    parentName: childReg.parentName,
                    firstName: child?.firstName || childReg.childName?.split(' ')?.[0],
                    lastName: child?.lastName || childReg.childName?.split(' ')?.slice(1).join(' '),
                    age: child?.age,
                    profileImage: child?.profileImage,
                    dateOfBirth: child?.dateOfBirth,
                    ...(child || {}),
                };
            });
            children = (await Promise.all(childPromises)).filter(Boolean);
        }

        return { children };
    } catch (error) {
        console.error('Error loading session data:', error);
        return { children: [] };
    }
};

export const loadSessionSummary = async (
    sessionId: string,
    sessionDays: string[] = [],
    registeredUsers: any[] = []
) => {
    try {
        if (!sessionId) return { summary: EMPTY_SUMMARY, needsCleanupSave: false };

        const studentIds = (registeredUsers || []).map((u: any) => u.userId).filter(Boolean);
        const [sessionActivity, studentActivities, dailyActivities] = await Promise.all([
            DataOps.fetchSessionActivity(sessionId),
            DataOps.fetchStudentActivitiesForSession(sessionId, studentIds),
            Promise.all((sessionDays || []).map((date) => DataOps.fetchDailyActivityForSessionDate(sessionId, date))),
        ]);

        const studentActivityById = new Map<string, any>();
        (studentActivities || []).forEach((item: any) => {
            const sid = item?.studentId;
            if (sid) studentActivityById.set(sid, item);
        });

        const summary: any = { attendance: {}, classData: {}, studentData: {} };
        const authorFallback = 'Coach';

        (sessionDays || []).forEach((date, idx) => {
            const daily = dailyActivities?.[idx] || null;
            const classCommentsRaw = sessionActivity?.dailyActivity?.[date]?.classComments
                || daily?.classComments
                || [];
            const classMomentsRaw = sessionActivity?.dailyActivity?.[date]?.classMoments
                || daily?.classMoments
                || [];

            summary.classData[date] = {
                comments: (classCommentsRaw || []).map((c: any) => toCommentUI(c)),
                photos: (classMomentsRaw || []).map((m: any) => toPhotoUI(m)),
            };

            summary.attendance[date] = summary.attendance[date] || {};

            studentIds.forEach((studentId) => {
                const studentDaily = studentActivityById.get(studentId)?.dailyActivity?.[date] || {};
                const dailyStudent = daily?.studentActivity?.[studentId] || {};
                const attendance = normalizeAttendanceRecord(
                    studentDaily?.attendance || dailyStudent?.attendance
                );

                summary.attendance[date][studentId] = attendance;
                summary.studentData[studentId] = summary.studentData[studentId] || {};
                summary.studentData[studentId][date] = {
                    comments: (studentDaily?.comments || dailyStudent?.comments || []).map((c: any) => toCommentUI(c)),
                    photos: (studentDaily?.moments || dailyStudent?.moments || []).map((m: any) => toPhotoUI(m)),
                    attendance,
                };
            });
        });

        return { summary, needsCleanupSave: false };
    } catch (error) {
        console.error('Error loading session summary:', error);
        return { summary: EMPTY_SUMMARY, needsCleanupSave: false };
    }
};

export const saveSessionSummary = async (sessionId: string, newSummary: any) => {
    // Kept for compatibility with current modal flow.
    return true;
};

export const uploadImageToFirebaseStorage = async (dataUri: string, sessionId: string): Promise<string> => {
    try {
        if (!sessionId) {
            throw new Error('Session ID is undefined - cannot upload image');
        }
        
        const response = await fetch(dataUri);
        const blob = await response.blob();
        const timestamp = Date.now();
        const filename = `session_photos/${sessionId}/${timestamp}.jpg`;
        const storage = getStorage();
        const storageRef = ref(storage, filename);
        
        await uploadBytes(storageRef, blob);
        const downloadUrl = await getDownloadURL(storageRef);
        return downloadUrl;
    } catch (error) {
        console.error('Error uploading image to storage:', error);
        throw error;
    }
};

export const handleAddComment = async (
    text: string,
    currentModalContext: any,
    isCoach: boolean,
    currentUser: any,
    sessionSummary: any,
    registeredUsers: any[],
    session: any,
    sessionId: string | undefined,
    campId: string | undefined,
    saveClassDataFn: (date: string, type: string, data: any) => Promise<void>,
    saveStudentDataFn: (userId: string, date: string, type: string, data: any) => Promise<void>
) => {
    if (!currentModalContext) return;

    // Check permissions
    if (!isCoach && currentModalContext.type === 'class') {
        Alert.alert('Permission Denied', 'You can only add comments to your own profile.');
        return;
    }

    if (!isCoach && currentModalContext.type === 'student' && currentModalContext.userId !== currentUser?.userId) {
        Alert.alert('Permission Denied', 'You can only add comments to your own profile.');
        return;
    }

    const authorName = `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim() || currentUser?.userName || 'Coach';
    const newComment = {
        id: Date.now().toString(),
        text,
        timestamp: new Date().toISOString(),
        author: isCoach ? authorName : (currentUser?.name || 'Camper')
    };

    if (currentModalContext.type === 'class') {
        const existingComments = sessionSummary.classData[currentModalContext.date]?.comments || [];
        const nextComments = [...existingComments, newComment];
        const cloudComments = nextComments.map((c: any) => toCloudComment(c, authorName));
        const saveOk = await DataOps.updateClassActivityBatch(
            sessionId || session?.sessionId,
            currentModalContext.date,
            {
                sessionId: sessionId || session?.sessionId,
                classComments: cloudComments,
                updatedAt: new Date(),
                updatedBy: currentUser?.userId,
            },
            {
                sessionId: sessionId || session?.sessionId,
                dailyActivity: {
                    [currentModalContext.date]: {
                        classComments: cloudComments,
                    }
                },
                updatedAt: new Date(),
                updatedBy: currentUser?.userId,
            }
        );
        if (!saveOk) {
            Alert.alert('Error', 'Failed to save class comment. Please try again.');
            return;
        }
        await saveClassDataFn(currentModalContext.date, 'comments', nextComments);
    } else {
        const existingComments = sessionSummary.studentData[currentModalContext.userId!]?.[currentModalContext.date]?.comments || [];
        const nextComments = [...existingComments, newComment];
        const cloudComments = nextComments.map((c: any) => toCloudComment(c, authorName));
        const saveOk = await DataOps.updateActivityBatch(
            sessionId || session?.sessionId,
            currentModalContext.userId!,
            currentModalContext.date,
            {
                sessionId: sessionId || session?.sessionId,
                studentActivity: {
                    [currentModalContext.userId!]: {
                        comments: cloudComments,
                    }
                },
                updatedAt: new Date(),
                updatedBy: currentUser?.userId,
            },
            {
                studentId: currentModalContext.userId!,
                sessionId: sessionId || session?.sessionId,
                dailyActivity: {
                    [currentModalContext.date]: {
                        comments: cloudComments,
                    }
                },
                updatedAt: new Date(),
                updatedBy: currentUser?.userId,
            }
        );
        if (!saveOk) {
            Alert.alert('Error', 'Failed to save student comment. Please try again.');
            return;
        }
        await saveStudentDataFn(currentModalContext.userId!, currentModalContext.date, 'comments', nextComments);
        
        // Send notification
        if (isCoach && currentModalContext.userId !== currentUser?.userId) {
            try {
                const childData = registeredUsers.find(u => u.userId === currentModalContext.userId);
                const parentUserId = childData?.parentId;
                
                if (parentUserId) {
                    const coachName = currentUser?.name || 'Coach';
                    const sessionTitle = session?.title || 'Session';
                    const formattedDate = NotificationOps.formatNotificationDate(currentModalContext.date);
                    const childName = childData?.childName || childData?.userName || 'Your child';
                    
                    await NotificationOps.notifyStudentComment(
                        parentUserId,
                        coachName,
                        sessionTitle,
                        formattedDate,
                        sessionId || session?.sessionId,
                        campId || session?.campId,
                        currentModalContext.userId!,
                        currentModalContext.date,
                        newComment.id,
                        childName
                    );
                }
            } catch (error) {
                console.error('Error sending comment notification:', error);
            }
        }
    }
};

export const handleAddPhoto = async (
    uri: string,
    caption: string | undefined,
    currentModalContext: any,
    isCoach: boolean,
    currentUser: any,
    sessionSummary: any,
    registeredUsers: any[],
    session: any,
    sessionId: string | undefined,
    campId: string | undefined,
    saveClassDataFn: (date: string, type: string, data: any) => Promise<void>,
    saveStudentDataFn: (userId: string, date: string, type: string, data: any) => Promise<void>
) => {
    if (!currentModalContext || !uri) return;

    // Check permissions
    if (!isCoach && currentModalContext.type === 'class') {
        Alert.alert('Permission Denied', 'You can only add photos to your own profile.');
        return;
    }

    if (!isCoach && currentModalContext.type === 'student' && currentModalContext.userId !== currentUser?.userId) {
        Alert.alert('Permission Denied', 'You can only add photos to your own profile.');
        return;
    }

    try {
        const downloadUrl = await uploadImageToFirebaseStorage(uri, sessionId || session?.sessionId);
        const authorName = `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim() || currentUser?.userName || 'Coach';
        
        const newPhoto = {
            id: Date.now().toString(),
            uri: downloadUrl,
            caption,
            timestamp: new Date().toISOString(),
            author: isCoach ? authorName : (currentUser?.name || 'Camper')
        };

        if (currentModalContext.type === 'class') {
            const existingPhotos = sessionSummary.classData[currentModalContext.date]?.photos || [];
            const nextPhotos = [...existingPhotos, newPhoto];
            const cloudMoments = nextPhotos.map((p: any) => toCloudMoment(p, authorName));
            const saveOk = await DataOps.updateClassActivityBatch(
                sessionId || session?.sessionId,
                currentModalContext.date,
                {
                    sessionId: sessionId || session?.sessionId,
                    classMoments: cloudMoments,
                    updatedAt: new Date(),
                    updatedBy: currentUser?.userId,
                },
                {
                    sessionId: sessionId || session?.sessionId,
                    dailyActivity: {
                        [currentModalContext.date]: {
                            classMoments: cloudMoments,
                        }
                    },
                    updatedAt: new Date(),
                    updatedBy: currentUser?.userId,
                }
            );
            if (!saveOk) {
                Alert.alert('Error', 'Failed to save class photo. Please try again.');
                return;
            }
            await saveClassDataFn(currentModalContext.date, 'photos', nextPhotos);
        } else {
            const existingPhotos = sessionSummary.studentData[currentModalContext.userId!]?.[currentModalContext.date]?.photos || [];
            const nextPhotos = [...existingPhotos, newPhoto];
            const cloudMoments = nextPhotos.map((p: any) => toCloudMoment(p, authorName));
            const saveOk = await DataOps.updateActivityBatch(
                sessionId || session?.sessionId,
                currentModalContext.userId!,
                currentModalContext.date,
                {
                    sessionId: sessionId || session?.sessionId,
                    studentActivity: {
                        [currentModalContext.userId!]: {
                            moments: cloudMoments,
                        }
                    },
                    updatedAt: new Date(),
                    updatedBy: currentUser?.userId,
                },
                {
                    studentId: currentModalContext.userId!,
                    sessionId: sessionId || session?.sessionId,
                    dailyActivity: {
                        [currentModalContext.date]: {
                            moments: cloudMoments,
                        }
                    },
                    updatedAt: new Date(),
                    updatedBy: currentUser?.userId,
                }
            );
            if (!saveOk) {
                Alert.alert('Error', 'Failed to save student photo. Please try again.');
                return;
            }
            await saveStudentDataFn(currentModalContext.userId!, currentModalContext.date, 'photos', nextPhotos);
            
            // Send notification
            if (isCoach && currentModalContext.userId !== currentUser?.userId) {
                try {
                    const childData = registeredUsers.find(u => u.userId === currentModalContext.userId);
                    const parentUserId = childData?.parentId;
                    
                    if (parentUserId) {
                        const coachName = currentUser?.name || 'Coach';
                        const sessionTitle = session?.title || 'Session';
                        const formattedDate = NotificationOps.formatNotificationDate(currentModalContext.date);
                        const childName = childData?.childName || childData?.userName || 'Your child';
                        
                        await NotificationOps.notifyStudentPhoto(
                            parentUserId,
                            coachName,
                            sessionTitle,
                            formattedDate,
                            sessionId || session?.sessionId,
                            campId || session?.campId,
                            currentModalContext.userId!,
                            currentModalContext.date,
                            newPhoto.id,
                            childName
                        );
                    }
                } catch (error) {
                    console.error('Error sending photo notification:', error);
                }
            }
        }
    } catch (error) {
        console.error('Error in handleAddPhoto:', error);
        Alert.alert('Error', 'Failed to save photo. Please try again.');
    }
};

export const toggleAttendance = async (
    userId: string,
    date: string,
    type: AttendanceUpdateType,
    sessionSummary: any,
    registeredUsers: any[],
    session: any,
    sessionId: string | undefined,
    campId: string | undefined,
    currentUser: any,
    saveSessionSummaryFn: (summary: any) => Promise<void>
) => {
    const previousAttendance = normalizeAttendanceRecord(sessionSummary.attendance?.[date]?.[userId]);
    const { nextAttendance, hasAttendanceChanged } = computeNextAttendance(previousAttendance, type);
    if (!hasAttendanceChanged) return sessionSummary;

    const newSummary = {
        ...sessionSummary,
        attendance: {
            ...sessionSummary.attendance,
            [date]: {
                ...sessionSummary.attendance[date],
                [userId]: nextAttendance
            }
        },
        studentData: {
            ...sessionSummary.studentData,
            [userId]: {
                ...(sessionSummary.studentData?.[userId] || {}),
                [date]: {
                    ...(sessionSummary.studentData?.[userId]?.[date] || {}),
                    attendance: nextAttendance,
                },
            },
        }
    };

    const saved = await DataOps.updateActivityBatch(
        sessionId || session?.sessionId,
        userId,
        date,
        {
            sessionId: sessionId || session?.sessionId,
            studentActivity: {
                [userId]: {
                    attendance: nextAttendance,
                }
            },
            updatedAt: new Date(),
            updatedBy: currentUser?.userId,
        },
        {
            studentId: userId,
            sessionId: sessionId || session?.sessionId,
            dailyActivity: {
                [date]: {
                    attendance: nextAttendance,
                }
            },
            updatedAt: new Date(),
            updatedBy: currentUser?.userId,
        }
    );

    if (!saved) {
        Alert.alert('Error', 'Failed to update attendance. Please try again.');
        return sessionSummary;
    }

    await saveSessionSummaryFn(newSummary);
    
    // Send notification
    if (nextAttendance.checkedIn && !previousAttendance.checkedIn) {
        try {
            const child = registeredUsers.find(u => u.userId === userId || u.childId === userId);
            
            if (child && child.parentId) {
                const coachName = currentUser?.userName || currentUser?.name || 'Coach';
                const sessionTitle = session?.title || 'Session';
                const formattedDate = NotificationOps.formatNotificationDate(date);
                const childName = child.childName || child.userName || 'Your child';
                
                await NotificationOps.notifyAttendance(
                    child.parentId,
                    childName,
                    sessionTitle,
                    formattedDate,
                    sessionId || session?.sessionId,
                    campId || session?.campId,
                    userId,
                    date
                );
            }
        } catch (error) {
            console.error('Error sending attendance notification:', error);
        }
    }

    return newSummary;
};

